/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'

import colors from '@sportninja/common/constants/appColors'
import type { PlannedSkip } from '@sportninja/common/features/game/gameListView/bulkPlan'

import { font, weightSemiBold } from 'src/components/css'
import type { BulkRunSummary, RowResult } from '../hooks/useGameBulkEdit'
import { gameViewsLabel } from '../utils/i18n'

export interface BulkFailurePanelProps {
  summary: BulkRunSummary
  /** Renders the row label for a game id — the panel has no row model itself. */
  describeRow: (gameId: string) => string
  onDismiss: () => void
}

const OUTCOME_LABEL: Record<RowResult['outcome'], string> = {
  saved: 'bulkOutcomeSaved',
  rejected: 'bulkOutcomeRejected',
  'not-saved': 'bulkOutcomeNotSaved',
  conflict: 'bulkOutcomeConflict',
  unconfirmed: 'bulkOutcomeUnconfirmed',
}

const SKIP_LABEL: Record<PlannedSkip['reason'], string> = {
  'no-change': 'bulkSkipNoChange',
  'missing-start': 'bulkSkipMissingStart',
  'nonexistent-local-time': 'bulkSkipNonexistentTime',
  'tbd-not-shiftable-by-minutes': 'bulkSkipTbdShift',
  'venue-required-for-facility': 'bulkSkipVenueRequired',
  'facility-not-in-venue': 'bulkSkipFacilityVenue',
  'unknown-venue': 'bulkSkipUnknownVenue',
  'inactive-facility': 'bulkSkipInactiveFacility',
  'invalid-day-shift': 'bulkSkipInvalidDayShift',
  'official-role-outside-bucket': 'bulkSkipOfficialOutsideBucket',
  'division-required-for-placeholder-team': 'bulkSkipPlaceholderDivision',
}

/**
 * The durable record of a run.
 *
 * A toast cannot be the only report: "we could not confirm whether this saved"
 * is exactly the state an admin has to act on later, and it must survive longer
 * than four seconds. Rejected, conflicted and unconfirmed are shown as three
 * DIFFERENT things because they call for three different responses — fix the
 * input, look at what someone else did, or go and check.
 */
const BulkFailurePanel = ({
  summary,
  describeRow,
  onDismiss,
}: BulkFailurePanelProps) => {
  'use memo'

  const problems = summary.results.filter(
    (result) => result.outcome !== 'saved'
  )
  const reportableSkips = summary.skipped.filter(
    (skip) => skip.reason !== 'no-change'
  )
  const unchanged = summary.skipped.length - reportableSkips.length

  /*
   * A run where everything saved has nothing to itemise.
   *
   * It still shows a bar, because dismissing it is what releases rows retained
   * outside the filter and unfreezes the sort — but a strip of zeroes ("0
   * rejected · 0 not saved · 0 conflicts · 0 unconfirmed") after every
   * successful edit is noise, and noise trains people to dismiss this panel
   * without reading it. That is the one habit it must not build, because when
   * it DOES have something to say, the edit silently did not save.
   */
  const isClean = problems.length === 0 && reportableSkips.length === 0

  return (
    <section
      css={panelStyles}
      // Announced once the run settles, not on every row — a per-row live
      // region would read a hundred updates at a screen reader.
      role='status'
      aria-live='polite'
      data-testid='bulk-failure-panel'
    >
      <header css={headerStyles}>
        <h3 css={titleStyles}>
          {gameViewsLabel('bulkSummaryTitle', { saved: summary.saved })}
        </h3>
        <button type='button' css={dismissStyles} onClick={onDismiss}>
          {gameViewsLabel('bulkDismiss')}
        </button>
      </header>

      {!isClean && (
        <p css={countsStyles}>
          {gameViewsLabel('bulkSummaryCounts', {
            saved: summary.saved,
            //  `not-saved` gets its own count. Folding it into `rejected` made the
            //  headline claim the API refused the edit when reconciliation had
            //  established the opposite — that it was never applied at all — and
            //  the two are acted on differently. Leaving it out entirely made the
            //  counts add up to less than the run, so it is stated, separately.
            rejected: summary.rejected,
            notSaved: summary.notSaved,
            conflicted: summary.conflicted,
            unconfirmed: summary.unconfirmed,
            unchanged,
          })}
        </p>
      )}

      {problems.length > 0 && (
        <ul css={listStyles}>
          {problems.map((result) => (
            <li key={result.gameId} css={itemStyles(result.outcome)}>
              <span css={rowLabelStyles}>{describeRow(result.gameId)}</span>
              <span css={outcomeStyles}>
                {gameViewsLabel(OUTCOME_LABEL[result.outcome])}
              </span>
              {result.invalidFields !== undefined && (
                <span css={detailStyles}>
                  {Object.entries(result.invalidFields)
                    .map(([field, message]) => `${field}: ${message}`)
                    .join(' · ')}
                </span>
              )}
              {result.invalidFields === undefined && result.message && (
                <span css={detailStyles}>{result.message}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {reportableSkips.length > 0 && (
        <ul css={listStyles}>
          {reportableSkips.map((skip) => (
            <li
              key={`${skip.gameId}-${skip.reason}`}
              css={itemStyles('rejected')}
            >
              <span css={rowLabelStyles}>{describeRow(skip.gameId)}</span>
              <span css={detailStyles}>
                {gameViewsLabel(SKIP_LABEL[skip.reason])}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const panelStyles = css`
  ${font.body}
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  background: ${colors.SECONDARY_400};
  padding: 12px 16px;
  margin-bottom: 12px;
`

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const titleStyles = css`
  ${weightSemiBold}
  font-size: 14px;
  color: ${colors.NEUTRAL_0};
  margin: 0;
`

const dismissStyles = css`
  ${font.body}
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid ${colors.SECONDARY_100};
  background: transparent;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;
`

const countsStyles = css`
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
  margin: 8px 0 0;
`

const listStyles = css`
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
`

const itemStyles = (outcome: RowResult['outcome']) => css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: baseline;
  font-size: 12px;
  color: ${colors.NEUTRAL_0};
  padding-left: 8px;
  border-left: 3px solid
    ${outcome === 'rejected' || outcome === 'not-saved'
      ? colors.ERROR_200
      : colors.WARNING_200};
`

const rowLabelStyles = css`
  ${weightSemiBold}
`

const outcomeStyles = css`
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.4px;
`

const detailStyles = css`
  color: ${colors.NEUTRAL_50};
  flex: 1 1 100%;
`

export default BulkFailurePanel
